import { Role } from "@cryptors/api-client/enums";
import { AuthProvider } from "@refinedev/core";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { postLogin } from "@/api/auth.api";
import queryClient from "@/queryClient";

const TOKEN_KEY = "accessToken";

type DecodedToken = {
  role: Role;
  email?: string;
  pseudo?: string;
  sub?: number;
  id?: number;
};

const decode = (): DecodedToken | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
};

export const getCurrentRole = (): Role | null => decode()?.role ?? null;

// Stable per-user key used to scope client-side caches. Without it, react-query
// keys like ["stats","comparison"] are shared across accounts, so one user's
// cached (e.g. unscoped, internal-role) data bleeds into the next account that
// logs in within the same tab. Falls back to "anon" when no token is present.
export const getCurrentUserKey = (): string => {
  const u = decode();
  return String(u?.id ?? u?.sub ?? "anon");
};

// Roles allowed into the backoffice and where each lands after login.
const BACKOFFICE_ROLES: readonly Role[] = [
  Role.ADMIN,
  Role.REDACTEUR,
  Role.ANALYSTE,
  Role.CLIENT,
];
const landingFor = (role: Role): string => {
  switch (role) {
    case Role.ADMIN:
      return "/users";
    case Role.REDACTEUR:
      return "/cryptors";
    default:
      // ANALYSTE / CLIENT are dashboard-first.
      return "/dashboard";
  }
};

const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    const { accessToken } = await postLogin({ email, password });
    if (!accessToken) {
      return {
        success: false,
        error: { name: "Login Error", message: "Invalid email or password" },
      };
    }
    const user = jwtDecode<DecodedToken>(accessToken);
    if (!BACKOFFICE_ROLES.includes(user.role)) {
      return {
        success: false,
        error: {
          name: "Login Error",
          message: "You are not allowed to access the backoffice",
        },
      };
    }
    // Refine resource queries are not keyed by user. Clear the previous
    // account's cache before activating this session.
    queryClient.removeQueries();
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    localStorage.setItem(TOKEN_KEY, accessToken);
    return { success: true, redirectTo: landingFor(user.role) };
  },
  check: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return { authenticated: false, redirectTo: "/login" };
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    return { authenticated: true };
  },
  logout: async () => {
    queryClient.removeQueries();
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
    return { success: true, redirectTo: "/login" };
  },
  onError: async (error) => {
    // Only an expired/invalid session (401) forces logout. A 403 now just means
    // the current role lacks permission for that action — surface it, don't
    // eject the user (partial-permission roles hit 403s legitimately).
    if (error?.response?.status === 401) {
      return { logout: true, redirectTo: "/login" };
    }
    return { error };
  },
  getPermissions: async () => decode()?.role ?? null,
  getIdentity: async () => {
    const u = decode();
    if (!u) return null;
    return {
      id: u.id ?? u.sub,
      name: u.pseudo ?? u.email ?? "Admin",
      email: u.email,
      role: u.role,
    };
  },
};

export default authProvider;
